import { IManageProduct, IManageProductAction } from 'models/product/manageproduct';
import { IProduct, IProductForm } from 'models/product/product';
import { IProductTagDomainValue, ICropImageData, IImageData, IProductAttributes } from 'models/common';
import { IStore } from 'redux/IStore';
import * as i18n from 'i18next';
import * as c from 'models/constants';
import { actions as notifActions } from 'redux-notifications';
import { browserHistory } from 'react-router';
import { change, reset } from 'redux-form';
const { notifSend } = notifActions;

/** Modal Module actions */
import { closeModal } from 'modules/modal';
import { lowerFirstObjectPropertiesNames, uniq, upperFirstObjectPropertiesNames } from '../../../../helpers/common';
import { ShopStatus } from '../../../../models';
import { IShopCard } from '../../../../models/shop/shopcard';
import { requestHelper } from 'redux/helpers';
import { attributesToShow, contentUnit } from 'helpers/products';

/** Action Types */
export const REQUEST_START: string = 'manageproduct/REQUEST_START';
export const REQUEST_FAILURE: string = 'manageproduct/REQUEST_FAILURE';
export const CLEAR_FORM_DATA: string = 'manageproduct/CLEAR_FORM_DATA';
export const GET_EDIT_PRODUCT_SUCCESS: string = 'manageproduct/GET_EDIT_PRODUCT_SUCCESS';
export const GET_PRODUCT_TAGS_SUCCESS: string = 'manageproduct/GET_PRODUCT_TAGS_SUCCESS';
export const GET_CATEGORY_SIZES_SUCCESS: string = 'manageproduct/GET_CATEGORY_SIZES_SUCCESS';
export const CLEAR_FORM_ERROR: string = 'manageproduct/CLEAR_FORM_ERROR';
export const SET_SHOP_STATUS: string = 'manageproduct/SET_SHOP_STATUS';

/** Initial State */
const initialState: IManageProduct = {
	isFetching: false,
	editProducts: [],
	categorySizes: [],
	shopStatus: null,
};

/** Reducer */
export function manageProductReducer(state = initialState, action: IManageProductAction): IManageProduct {
	switch (action.type) {
		case REQUEST_START:
			return {
				...state,
				isFetching: true,
				error: false,
			};

		case REQUEST_FAILURE:
			return {
				...state,
				isFetching: false,
				message: action.payload.message,
				error: true,
			};

		case CLEAR_FORM_DATA:
			return {
				...state,
				editProducts: [],
				shopStatus: null,
				isFetching: false,
				error: false,
				message: undefined,
			};

		case GET_EDIT_PRODUCT_SUCCESS:
			return {
				...state,
				editProducts: action.payload.editProducts,
				isFetching: false,
			};

		case GET_PRODUCT_TAGS_SUCCESS:
			return {
				...state,
				isFetching: false,
				productTags: action.payload.productTags,
			};

		case GET_CATEGORY_SIZES_SUCCESS:
			return {
				...state,
				isFetching: false,
				categorySizes: action.payload.categorySizes,
			};

		case CLEAR_FORM_ERROR:
			return {
				...state,
				isFetching: false,
				message: null,
				error: false,
			};

		case SET_SHOP_STATUS:
			return {
				...state,
				shopStatus: action.payload.shopStatus,
			};

		default:
			return state;
	}
}

export function getProductVariants(variantParentId: string) {
	if (!variantParentId) {
		console.error('Missing variant parent ID');
		return;
	}
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return requestHelper<IProduct[]>(
			fetch(
				getState().fetchDefault.baseAPI + 'api/Product/ProductVariants/' + variantParentId,
				getState().fetchDefault.requestInit,
			),
		)
			.then((variations) => {
				const sizekey: keyof IProductAttributes = 'SizeKey';
				variations.forEach((v) => {
					if (v.attributes && sizekey in v.attributes) {
						v.content = v.attributes.SizeKey;
					}
				});
				dispatch(getEditProductSuccess(variations));
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

function compareKeys(a: any, b: any, exclude: string[] = []): boolean {
	const allKeys = uniq([...Object.keys(a), ...Object.keys(b)]);
	for (const key of allKeys) {
		if (exclude.includes(key as any)) {
			continue;
		}
		if (a[key] !== b[key]) {
			return true;
		}
	}
	return false;
}

function anyChange(a: IProduct, b: IProduct): boolean {
	const ignoredKeys: Array<keyof (IProductForm & IProduct)> = [
		'id',
		'attributes',
		'variantParentId',
		'variations',
		'hasVariations',
		'firstDropdownCategoryKey',
		'secondDropdownCategoryKey',
		'thirdDropdownCategoryKey',
		'selectedImages',
		'selectedThumbnail',
		'images',
		'shopStatus',
		'shopId',
	];
	if (compareKeys(a, b, ignoredKeys)) {
		return true;
	}
	if (
		compareKeys(
			lowerFirstObjectPropertiesNames(a.attributes || {}),
			lowerFirstObjectPropertiesNames(b.attributes || {}),
		)
	) {
		return true;
	}
	return false;
}

export function submitForm(rawForm: IProductForm, shopId: string, mode: 'add' | 'edit') {
	const form: IProductForm = {
		...rawForm,
		variations: rawForm.hasVariations ? rawForm.variations : [rawForm.variations[0] || {}],
	};
	return (dispatch, getState: () => IStore) => {
		let promise: Promise<any> = Promise.resolve(true);
		const previous = getState().manageProduct.editProducts || [];
		if (mode === 'add') {
			promise = createGroupedProduct(getState, shopId, form);
		} else {
			if (!previous.length) {
				console.error('No previous state');
			}
			const promises: Array<Promise<any>> = [];
			const variations = createVariations(form, shopId);
			const variantParentId = variations.find((v) => v.variantParentId).variantParentId;
			if (!variantParentId) {
				promise = Promise.reject('No variantParentId');
			} else {
				const currentIds: string[] = [];
				for (const variation of variations) {
					let existing: IProduct = null;
					if (variation.id) {
						existing = previous.find((p) => p.id === variation.id);
					}
					if (existing) {
						currentIds.push(variation.id);
						if (anyChange(existing, variation)) {
							promises.push(changeProductRequest(getState, variation, shopId));
						}
					} else {
						promises.push(createProductRequest(getState, variation, shopId));
					}
				}
				const previousIds = previous.map((p) => p.id).filter(Boolean);
				for (const prevId of previousIds) {
					if (!currentIds.includes(prevId)) {
						promises.push(deleteProductRequest(getState, prevId));
					}
				}
				promise = Promise.all(promises).catch((e) =>
					dispatch(getProductVariants(variantParentId)).then(() => {
						throw e;
					}),
				);
			}
		}

		return promise
			.then(() => {
				dispatch(reset(c.FORM_NAME_EDIT_PRODUCT));
				browserHistory.push(`/shop/${shopId}/edit/products`);
			})
			.catch((message) => {
				dispatch(requestFailure(message));
			});
	};
}

function createVariations(form: IProductForm, shopId: string): IProduct[] {
	const variations: IProduct[] = [];
	const categoryKey = Number(form.attributes?.CategoryKey);
	if (!categoryKey) {
		throw new Error('Missing category');
	}
	for (const v of form.variations) {
		const attributes: IProductAttributes = {};
		const copyAttrsFromMain = attributesToShow(form, 'main');
		const copyAttrsFromVariation = attributesToShow(form, 'variations');
		for (const attr of copyAttrsFromMain) {
			attributes[attr] = (form.attributes || {})[attr];
		}
		for (const attr of copyAttrsFromVariation) {
			attributes[attr] = (v.attributes || {})[attr];
		}
		attributes.CategoryKey = form.attributes.CategoryKey;
		if (contentUnit(attributes.CategoryKey)) {
			attributes.SizeKey = v.content;
		}
		const copyFromMain: Array<keyof IProductForm> = [
			'name',
			'description',
			'shopId',
			'published',
			'variantParentId',
			'showInBSPortal',
			'delivery',
			'metatagDescription',
			'metatagKeywords',
			'metatagTitle',
			'externalLink',
		];
		const p: IProduct = {
			shopId,
			// ...form,
			...v,
			description: form.description,
			name: form.name,
			variantParentId: form.variantParentId || v.variantParentId,
			attributes: upperFirstObjectPropertiesNames(attributes),
		};
		for (const key of copyFromMain) {
			p[key] = form[key];
		}
		variations.push(p);
	}
	return variations;
}

function deleteProductRequest(getState: () => IStore, productId: string) {
	return requestHelper(
		fetch(getState().fetchDefault.baseAPI + 'api/Product/' + productId, {
			...getState().fetchDefault.requestInit,
			method: 'DELETE',
		}),
	);
}

function createProductRequest(getState: () => IStore, product: IProduct, shopId: string): Promise<any> {
	const productWithShop = {
		...product,
		shopId,
	};
	return requestHelper(
		fetch(getState().fetchDefault.baseAPI + 'api/Product', {
			...getState().fetchDefault.requestInit,
			method: 'POST',
			body: JSON.stringify(productWithShop),
		}),
	);
}

function changeProductRequest(getState: () => IStore, product: IProduct, shopId: string): Promise<any> {
	const productWithShop: IProduct = {
		...product,
		attributes: upperFirstObjectPropertiesNames(product.attributes),
		shopId,
	};
	return requestHelper(
		fetch(getState().fetchDefault.baseAPI + 'api/Product/' + product.id, {
			...getState().fetchDefault.requestInit,
			method: 'PUT',
			body: JSON.stringify(productWithShop),
		}),
	);
}

function createGroupedProduct(getState: () => IStore, shopId: string, form: IProductForm): Promise<any> {
	const variations = createVariations(form, shopId);
	return requestHelper(
		fetch(getState().fetchDefault.baseAPI + 'api/Product/ProductVariants?shopId=' + shopId, {
			...getState().fetchDefault.requestInit,
			method: 'POST',
			body: JSON.stringify(variations),
		}),
	);
}

/** Action Creator */
export function getProductTags() {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/ProductTag',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res) => dispatch(getProductTagsSuccess(res)));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function getShopStatus(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/shop/' + id,
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IShopCard) => {
						dispatch(setShopStatus(res.status));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Async Action Creator */
export function deleteProduct(id: string, shopId: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());
		return deleteProductRequest(getState, id)
			.then(() => {
				dispatch(getEditProductSuccess(initialState.editProducts));
				dispatch(
					notifSend({ message: i18n.t('Notification_Success_Product_Deleted'), kind: 'success', dismissAfter: 3000 }),
				);
				dispatch(reset(c.FORM_NAME_EDIT_PRODUCT));
				browserHistory.push(`/shop/${shopId}/edit/products`);
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator  */
export function cropAndSaveProductImage(image: ICropImageData, index: number, fieldName: string, formName: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/imageupload/crop',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((image: IImageData) => {
						dispatch(change(formName, `${fieldName}[${index}].href`, image.href));
						dispatch(change(formName, `${fieldName}[${index}].id`, image.id));
						dispatch(closeModal());
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): IManageProductAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IManageProductAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function clearFormData(): IManageProductAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

/** Action Creator */
export function getEditProductSuccess(editProducts: IProduct[]): IManageProductAction {
	return {
		type: GET_EDIT_PRODUCT_SUCCESS,
		payload: {
			editProducts,
		},
	};
}

/** Action Creator */
export function getProductTagsSuccess(productTags: IProductTagDomainValue[]): IManageProductAction {
	return {
		type: GET_PRODUCT_TAGS_SUCCESS,
		payload: {
			productTags,
		},
	};
}

/** Action Creator */
export function clearProductFormError(): IManageProductAction {
	return {
		type: CLEAR_FORM_ERROR,
	};
}

/** Action Creator */
export function setShopStatus(shopStatus: ShopStatus): IManageProductAction {
	return {
		type: SET_SHOP_STATUS,
		payload: {
			shopStatus,
		},
	};
}
